// coeditBroadcastMiddleware.js — bug 925: LIVE CO-EDIT's `broadcastEdit` was
// wired into `useWheelCoedit` (see the hook) but nothing ever CALLED it, so a
// local edit was never sent to peers over the coedit socket even though the
// socket itself connected fine. Measured live on web-stg (bundle
// main.e60d9707.js): editing the wheel title produced zero outbound WS "op"
// frames on the editor's own socket, and the peer's page never reflected the
// change without a manual reload.
//
// This middleware is the generic wiring point: whenever a WHEEL-LEVEL or
// SEGMENT-LEVEL edit action lands in the store, forward it to whatever coedit
// session is currently live (via coeditBroadcastRegistry — the socket is
// component-scoped, this middleware is not). Individual edit call sites
// (Details tab, segment settings, etc.) stay unaware that co-edit exists;
// they just dispatch the same redux action they always did.
//
// batch2-0925-P1-teams-segment-edit-live-apply-broadcast: the wheel-level
// scope above shipped, but a SEGMENT edit (title, fill, block content) never
// reached this middleware at all, so it never produced an outbound "op"
// frame — measured live on web-dev.iyuga.net: an Owner's segment-title save
// persisted server-side but the other collaborator's builder never
// live-applied it (see batch2-0925-P1-teams-live-coedit-2-users-after-6138's
// RESULT.md, cell 2). The receiving end (`applyRemoteOp`,
// redux/reducers/segmentBuilder.js) already fully supports a segment-scoped
// `{ scope: <segmentId>, patch }` op (merges `patch` onto the target segment)
// — it just never received one. This closes that gap for the three segment
// edit surfaces the bug asks for:
//   - title / fill / colour palette: updateSegmentSetting and
//     updateSegmentColorPalette both merge their change into the segment's
//     `settings`, so the broadcast sends the segment's whole POST-mutation
//     `settings` object — last-write-wins on the receiving end, matching
//     applyRemoteOp's plain merge, and correct regardless of which of the
//     three fields moved.
//   - block content: block edits (add/move/edit/reorder) all route through
//     the whole-array `setWheelSegments` replace, so instead of a dedicated
//     action per block op, this diffs the segment `nodes` (the JSON-encoded
//     block tree) before vs. after and broadcasts only the segment(s) whose
//     nodes actually changed. A brand-new segment (present after but not
//     before) is a structural add, not a content edit, and is not
//     broadcast as a content op — it goes out in the segment-list op below
//     (stgfix-10121833).
import { getCoeditBroadcast } from "../../hooks/coeditBroadcastRegistry";

const WHEEL_LEVEL_PATCH_BUILDERS = {
  "segmentBuilder/setWheelName": (payload) => ({ wheel_name: payload }),
  "segmentBuilder/setSubTitle": (payload) => ({ sub_title: payload }),
};

// Segment-scoped actions whose payload is `{ id, ... }` and whose change
// lands inside the target segment's `settings` (title, fill/wallpaper, and
// colour palette all live there).
const SEGMENT_SETTINGS_ACTIONS = new Set([
  "segmentBuilder/updateSegmentSetting",
  "segmentBuilder/updateSegmentColorPalette",
]);

// review FE#6932 (T22.126): Undo/Redo moves the builder to another history
// snapshot without any of the edit actions above, so peers never got an op for
// it. A View Wheel viewer (which re-applies recent live ops over its refetch)
// kept showing the undone title until reload — measured on web-stg
// main.91574a72, wheel 508011. Broadcast each segment whose settings or nodes
// the step changed. Snapshots are deep clones, so compare by value.
const UNDO_REDO_ACTIONS = new Set(["segmentBuilder/undo", "segmentBuilder/redo"]);

// stgfix-10121833 (T22.126): adding or removing a segment (and the Undo/Redo
// of one) changed the segment LIST without any op, so a View Wheel viewer saw
// it only after the save + refetch, ~7 s late (web-stg main.637e0397, wheel
// 508011). Broadcast the new list when its ids change: `{ id, value,
// position }` for a segment the list already had, the whole segment for an
// added one, plus the `removed` ids. No `patch`, so builder peers' applyRemoteOp ignores it; the View
// Wheel applies it (pages/ViewWheel/liveSegmentOps.js).
const SEGMENT_LIST_ACTIONS = new Set([
  "segmentBuilder/setWheelSegments",
  "segmentBuilder/removeWheelSegment",
  "segmentBuilder/undo",
  "segmentBuilder/redo",
]);

function segmentIds(segments) {
  return segments
    .filter((s) => s && s.id != null)
    .map((s) => String(s.id))
    .join(",");
}

function segmentListEntry(segment, known) {
  if (known) {
    return { id: segment.id, value: segment.value, position: segment.position };
  }
  // eslint-disable-next-line no-unused-vars
  const { initialNode, selected, ...rest } = segment;
  return {
    ...rest,
    nodes: rest.nodes != null ? rest.nodes : initialNode,
  };
}

function broadcastSegmentList(prevSegments, segments) {
  const session = getCoeditBroadcast();
  if (!session || typeof session.broadcastEdit !== "function") return;
  const prevIds = new Set(
    prevSegments.filter((s) => s && s.id != null).map((s) => String(s.id)),
  );
  const baseVersion =
    typeof session.getVersion === "function" ? session.getVersion() : undefined;
  const nextIds = new Set(
    segments.filter((s) => s && s.id != null).map((s) => String(s.id)),
  );
  session.broadcastEdit(
    {
      kind: "segment_list",
      scope: null,
      segments: segments
        .filter((s) => s && s.id != null)
        .map((s) => segmentListEntry(s, prevIds.has(String(s.id)))),
      removed: prevSegments
        .filter((s) => s && s.id != null && !nextIds.has(String(s.id)))
        .map((s) => s.id),
    },
    baseVersion,
    null,
  );
}

function sameJson(a, b) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) {
    return false;
  }
}

function currentSegments(state) {
  const sb = state && state.segmentBuilder;
  if (!sb || !Array.isArray(sb.history)) return [];
  const present = sb.history[sb.currentIndex];
  return (present && present.wheel_segments) || [];
}

function findSegment(segments, id) {
  return segments.find((s) => s && String(s.id) === String(id));
}

function broadcastOp(patch, scope) {
  const session = getCoeditBroadcast();
  if (!session || typeof session.broadcastEdit !== "function") return;
  const baseVersion =
    typeof session.getVersion === "function" ? session.getVersion() : undefined;
  session.broadcastEdit({ scope, patch }, baseVersion, null);
}

const coeditBroadcastMiddleware = (store) => (next) => (action) => {
  // Snapshot pre-mutation segments (by id) BEFORE next() for the
  // setWheelSegments nodes-diff below — must run before the reducer replaces
  // the array.
  const prevSegmentsById =
    action && action.type === "segmentBuilder/setWheelSegments"
      ? new Map(currentSegments(store.getState()).map((s) => [String(s.id), s]))
      : null;
  const undoRedoPrev =
    action && UNDO_REDO_ACTIONS.has(action.type)
      ? new Map(currentSegments(store.getState()).map((s) => [String(s.id), s]))
      : null;

  const listPrev =
    action && SEGMENT_LIST_ACTIONS.has(action.type)
      ? currentSegments(store.getState())
      : null;

  const result = next(action);

  if (listPrev) {
    const listNext = currentSegments(store.getState());
    if (segmentIds(listPrev) !== segmentIds(listNext)) {
      broadcastSegmentList(listPrev, listNext);
    }
  }

  if (undoRedoPrev) {
    currentSegments(store.getState()).forEach((segment) => {
      if (!segment || segment.id == null) return;
      const prev = undoRedoPrev.get(String(segment.id));
      if (!prev) return; // structural add/remove: not a content op
      if (!sameJson(prev.settings, segment.settings)) {
        broadcastOp({ settings: segment.settings }, segment.id);
      }
      if (!sameJson(prev.nodes, segment.nodes)) {
        broadcastOp({ nodes: segment.nodes }, segment.id);
      }
    });
  }

  const buildPatch = action && WHEEL_LEVEL_PATCH_BUILDERS[action.type];
  if (buildPatch) {
    broadcastOp(buildPatch(action.payload), null);
  }

  if (action && SEGMENT_SETTINGS_ACTIONS.has(action.type)) {
    const id = action.payload && action.payload.id;
    if (id != null) {
      const segment = findSegment(currentSegments(store.getState()), id);
      if (segment) broadcastOp({ settings: segment.settings }, id);
    }
  }

  if (prevSegmentsById) {
    const nextSegments = Array.isArray(action.payload) ? action.payload : [];
    nextSegments.forEach((segment) => {
      if (!segment || segment.id == null) return;
      const prev = prevSegmentsById.get(String(segment.id));
      if (prev && prev.nodes !== segment.nodes) {
        broadcastOp({ nodes: segment.nodes }, segment.id);
      }
    });
  }

  return result;
};

export default coeditBroadcastMiddleware;
